import { inject } from "@angular/core";
import { FmvEntityAttribute } from "../services/fmv-entity-attribute.service";
import { WindowService } from "../services/window.service";
import { BaseMapEntityCommand } from "../services/base-map-entity-command.service";
import { FmvDialogComponent } from "../../components/shared/details-footer-window/fmv-dialog/fmv-dialog.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DataContextService } from "../services/data-context.service";
import { ResponseModalComponent } from "../../components/shared/response-modal/response-modal.component";
import { CoreService } from "../services/core.service";
import * as Cesium from 'cesium';
import { TimelineService } from "../services/timeline.service";

export interface Command {
    name: string;
    id: string;
    icon: string;
    modesVisible: string[];
    shouldShow?: (mode: any) => boolean;
    init?: (entity: any) => void;
   execute?: (entity?: any) => any; 
  }
export class DefaultDeleteCommand implements Command {
  name = 'Delete';
  id = this.generateId();
  icon = 'assets/img/delete.svg';
  modesVisible: string[] = [];
  shouldShow = () => true;

  constructor(
    private modalService: NgbModal,
    private dataContext: DataContextService,
    private windowService: WindowService
  ) {}

  init = (entity: any) => {};

  execute = (mapEntity: any) => {
    this.showAlert('Are you absolutely sure you want to delete this entity from the map?').then((result) => {
      if (result) {
        mapEntity.onDelete();
        this.windowService.closeMapEntityDetailsWindow(`window-${mapEntity.id}`);
        this.dataContext.removeMapEntity(mapEntity);
      }
    });
  };

  private generateId() {
    return 'delete-' + Math.random().toString(36).substr(2, 9);
  }
    private showAlert(message: string) {
      const modalRef = this.modalService.open(ResponseModalComponent, {
        centered: true,
        windowClass: 'response-modal-top zindex',
        backdropClass: 'response-modal-backdrop-top',
      });
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.showCancel = true;
      return modalRef.result;
    }
}
  
  export class DefaultShowHideCommand implements Command {
    name = 'Show/Hide';
    id = this.generateId();
    icon = 'assets/img/eyeVisible.svg';
    modesVisible: string[] = [];
    shouldShow = () => true;

    init = (mapEntity: any) => {
      this.setShowHideIcon(mapEntity.isVisible());
    };

    execute = (mapEntity: any) => {
      if (mapEntity.isVisible()) {
        mapEntity.hide();
      } else {
        mapEntity.show();
      }
      this.setShowHideIcon(mapEntity.isVisible());
    };

    setShowHideIcon(show: boolean) {
      this.icon = show ? 'assets/img/eyeVisible.svg' : 'assets/img/eyeHidden.svg';
    }

    private generateId() {
      return 'showhide-' + Math.random().toString(36).substr(2, 9);
    }
  }
  export class DefaultFlyToCommand implements Command {
    name = 'Fly To';
    id = this.generateId();
    icon = 'assets/img/look.svg';
    modesVisible: string[] = [];
    shouldShow = () => true;

    init = (entity: any) => {};

    execute = (mapEntity: any) => {
      if (mapEntity && typeof mapEntity.goToEntity === 'function') {
        mapEntity.goToEntity();
      }
    };

    private generateId() {
      return 'flyto-' + Math.random().toString(36).substr(2, 9);
    }
  }
  export class DefaultEditCommand implements Command {
    name = 'Edit';
    id = this.generateId();
    icon = 'assets/img/edit.svg';
    modesVisible: string[] = [];
    shouldShow = (mode?: string) => {
      return !this.modesVisible.length || (!!mode && this.modesVisible.includes(mode));
    };

    init = (entity: any) => {};

    execute = (mapEntity: any, scope?: any) => {
      if (typeof mapEntity.showEditWindow === 'function') {
        mapEntity.showEditWindow(mapEntity);
      }
    };

    private generateId() {
      return 'edit-' + Math.random().toString(36).substr(2, 9);
    }

    setModesVisible(modesVisible: string[]): void {
      this.modesVisible = modesVisible;
    }
  }
export class DefaultGoToTimeCommand implements Command {
  name = 'Zoom To Timeline';
  id = this.generateId();
  icon = 'assets/img/timelinezoom.svg';
  modesVisible: string[] = [];
  shouldShow = () => true;

  constructor(private timelineService: TimelineService) {}

  init = (entity: any) => {};

  execute = (mapEntity: any) => {
    const timestamp = mapEntity.getTimestamp?.();
    if (timestamp !== undefined) {
      this.timelineService.setTime(timestamp);
    }
  };

  private generateId() {
    return 'showhide-' + Math.random().toString(36).substr(2, 9);
  }
}
export class FmvCommand implements Command {
  name = 'FMV';
  id = this.generateId();
  icon = 'assets/img/fmvfeed.svg';
  modesVisible: string[] = [];
  shouldShow = () => true;
  constructor(
    private windowService: WindowService,
  ) {}

  init = (mapEntity: any) => {
    this.setFmvIcon(mapEntity);
  };

  execute = (mapEntity: any) => {
    this.setFmvIcon(mapEntity);
     const modalPromise = this.windowService.openModalWindow(
      mapEntity,
      FmvDialogComponent,
      "",  undefined,
      undefined,
      undefined,
      { mapEntity },
      { windowClass: 'fmv-modal-top' } 
    );
    return modalPromise;
  };

  setFmvIcon(mapEntity: any) {
    if (this.entityHasFmvAttribute(mapEntity)) {
      this.icon = 'assets/img/fmvfeed_command_active.svg';
    } else {
      this.icon = 'assets/img/fmvfeed_command_none.svg';
    }
  }

  entityHasFmvAttribute(mapEntity: any): boolean {
    return (mapEntity.getAttributes() || []).some(
      (attr: any) => attr instanceof FmvEntityAttribute
    );
  }

  private generateId() {
    return 'fmv-' + Math.random().toString(36).substr(2, 9);
  }
}
export class DefaultTrackingCommand implements Command {
  name = 'Lock Camera';
  id = this.generateId();
  icon = 'assets/img/lock.svg';
  modesVisible: string[] = [];
  shouldShow = () => true;

  constructor(private core: CoreService) {}

  init = (entity: any) => {};

execute = (mapEntity: any) => {
  const cesiumEntity = mapEntity.getCesiumEntity();
  if (
    cesiumEntity.position &&
    typeof cesiumEntity.position.getValue !== 'function'
  ) {
    cesiumEntity.position = new Cesium.ConstantPositionProperty(cesiumEntity.position);
  }
  this.core.setTrackingEntity(mapEntity);
};

  setTrackingIcon(selected: boolean) {
    this.icon = 'assets/img/lock.svg';
  }

  private generateId() {
    return 'tracking-' + Math.random().toString(36).substr(2, 9);
  }
}